import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import type {
  BasePriceConfiguration,
  PriceConfigurationType,
  PriceDifferentiationModeType,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import {
  PRICE_COST_TYPE,
  PRICE_DIFFERENTIATION_MODE,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type { StockConfigurationType } from 'app/typings/core/common/openingConfiguration/stockConfigurationType';
import { ExperienceType } from 'app/typings/experiences';

import type { ExperienceInitialFormType } from '../useExperienceData';

// Error shapes consumed by the Availability components. Kept backward-compatible:
// - stock errors expose `days` (per-day highlight) and `message`
// - price errors expose `missingFields` (CONSTANT inline highlight), `fieldsByDay`
//   (DAILY inline highlight), `days` and `message`
// - guests errors expose `message`
export type ValidationError = {
  message: string;
  type?: string;
  days?: DayOfWeek[];
};

export type StockValidationError = ValidationError & {
  isClosed?: boolean;
  stock?: number | null;
};

export type PriceValidationError = ValidationError & {
  price?: number;
  missingFields?: string[];
  fieldsByDay?: Partial<Record<DayOfWeek, string[]>>;
};

export type ValidationErrorResult<T extends ValidationError = ValidationError> =
  {
    isValid: boolean;
    errors: T[];
  };

export type FormValidationErrors = {
  stockError: ValidationErrorResult<StockValidationError>;
  priceError: ValidationErrorResult<PriceValidationError>;
  guestsError: ValidationErrorResult<ValidationError>;
};

const ALL_DAYS = Object.values(DAY_OF_WEEK);

const isEmpty = (value: number | undefined | null): boolean =>
  value === undefined || value === null;

const valid = (): { isValid: true; errors: never[] } => ({
  isValid: true,
  errors: [],
});

// Mirrors the UI (PriceConfiguration) and core-service: an "original" is a
// country-scoped staycation activity (no hotel). Used to apply the stricter
// originals rules.
const isOriginalExperience = (formValues: ExperienceInitialFormType): boolean =>
  !formValues?.hotelId &&
  !!formValues?.countryId &&
  formValues?.experienceType === ExperienceType.STAYCATION;

const getStockForDay = (
  stock: StockConfigurationType | undefined,
  day: DayOfWeek
): { stock: number | null | undefined; isClosed: boolean } => {
  if (!stock) {
    return { stock: undefined, isClosed: false };
  }

  if (stock.type === OPENING_CONFIGURATION_TYPE.DAILY) {
    const dayConfig = stock.days?.[day];

    return {
      stock: dayConfig?.stock,
      isClosed: dayConfig?.isClosed ?? false,
    };
  }

  return { stock: stock.configuration?.stock, isClosed: false };
};

const getPriceForDay = (
  price: PriceConfigurationType,
  day: DayOfWeek
): BasePriceConfiguration =>
  price.type === OPENING_CONFIGURATION_TYPE.DAILY
    ? (price.days?.[day] ?? {})
    : (price.configuration ?? {});

// ============================================================================
// Price rules — faithful mirror of core-service adminExperience.service.ts
// (validatePriceDifferentiationMode l.590-762,
//  validateAlwaysIncludedWithNightConfiguration l.484-588,
//  validateStaycationExperience l.430-472). Returns the first violation
// message, or null when the day is valid.
// ============================================================================

const priceModeViolation = (
  config: BasePriceConfiguration,
  mode: PriceDifferentiationModeType,
  hasAdult: boolean,
  hasChild: boolean
): string | null => {
  switch (mode) {
    case PRICE_DIFFERENTIATION_MODE.NONE:
      if (hasAdult && isEmpty(config.price)) {
        return 'Price is required for ADULT pax type but not defined';
      }

      if (!hasAdult && !isEmpty(config.price)) {
        return 'Price is defined but ADULT pax type is not allowed';
      }

      if (
        !isEmpty(config.daytimePrice) ||
        !isEmpty(config.childPrice) ||
        !isEmpty(config.childDaytimePrice) ||
        !isEmpty(config.negotiatedPrice) ||
        !isEmpty(config.staycationPrice)
      ) {
        return 'Price differentiation mode is NONE but unexpected prices are defined';
      }

      return null;

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD:
      if (hasAdult && isEmpty(config.price)) {
        return 'Price is required for ADULT pax type but not defined';
      }

      if (!hasAdult && !isEmpty(config.price)) {
        return 'Price is defined but ADULT pax type is not allowed';
      }

      if (hasChild && isEmpty(config.childPrice)) {
        return 'Child price is required for CHILD pax type but not defined';
      }

      if (!hasChild && !isEmpty(config.childPrice)) {
        return 'Child price is defined but CHILD pax type is not allowed';
      }

      if (
        !isEmpty(config.daytimePrice) ||
        !isEmpty(config.childDaytimePrice) ||
        !isEmpty(config.negotiatedPrice) ||
        !isEmpty(config.staycationPrice)
      ) {
        return 'Price differentiation mode is WITH_CHILD but unexpected prices are defined';
      }

      return null;

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME:
      if (hasAdult && isEmpty(config.price)) {
        return 'Price is required for ADULT pax type but not defined';
      }

      if (!hasAdult && !isEmpty(config.price)) {
        return 'Price is defined but ADULT pax type is not allowed';
      }

      if (hasChild && isEmpty(config.childPrice)) {
        return 'Child price is required for CHILD pax type but not defined';
      }

      if (!hasChild && !isEmpty(config.childPrice)) {
        return 'Child price is defined but CHILD pax type is not allowed';
      }

      if (hasAdult && isEmpty(config.daytimePrice)) {
        return 'Daytime price is required for ADULT pax type but not defined';
      }

      if (!hasAdult && !isEmpty(config.daytimePrice)) {
        return 'Daytime price is defined but ADULT pax type is not allowed';
      }

      if (hasChild && isEmpty(config.childDaytimePrice)) {
        return 'Child daytime price is required for CHILD pax type but not defined';
      }

      if (!hasChild && !isEmpty(config.childDaytimePrice)) {
        return 'Child daytime price is defined but CHILD pax type is not allowed';
      }

      if (
        !isEmpty(config.negotiatedPrice) ||
        !isEmpty(config.staycationPrice)
      ) {
        return 'Price differentiation mode is WITH_CHILD_DAYTIME but unexpected prices are defined';
      }

      return null;

    case PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME:
      if (hasAdult && isEmpty(config.price)) {
        return 'Price is required for ADULT pax type but not defined';
      }

      if (!hasAdult && !isEmpty(config.price)) {
        return 'Price is defined but ADULT pax type is not allowed';
      }

      if (hasAdult && isEmpty(config.daytimePrice)) {
        return 'Daytime price is required for ADULT pax type but not defined';
      }

      if (!hasAdult && !isEmpty(config.daytimePrice)) {
        return 'Daytime price is defined but ADULT pax type is not allowed';
      }

      if (
        !isEmpty(config.childPrice) ||
        !isEmpty(config.childDaytimePrice) ||
        !isEmpty(config.negotiatedPrice) ||
        !isEmpty(config.staycationPrice)
      ) {
        return 'Price differentiation mode is WITH_DAYTIME but unexpected prices are defined';
      }

      return null;

    default:
      return null;
  }
};

const alwaysIncludedViolation = (
  config: BasePriceConfiguration,
  mode: PriceDifferentiationModeType,
  hasAdult: boolean,
  hasChild: boolean
): string | null => {
  if (
    mode !== PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME &&
    mode !== PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME
  ) {
    return 'Price differentiation mode is not WITH_DAYTIME or WITH_CHILD_DAYTIME for always included with night experience';
  }

  if (!isEmpty(config.price) || !isEmpty(config.childPrice)) {
    return `Price differentiation mode is ${mode} but non-daytime prices are defined for always included with night`;
  }

  if (hasAdult && isEmpty(config.daytimePrice)) {
    return 'Daytime price is required for ADULT pax type but not defined';
  }

  if (!hasAdult && !isEmpty(config.daytimePrice)) {
    return 'Daytime price is defined but ADULT pax type is not allowed';
  }

  if (mode === PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME) {
    if (hasChild && !isEmpty(config.childDaytimePrice)) {
      return 'Child daytime price should not be defined for WITH_DAYTIME mode';
    }

    return null;
  }

  if (hasChild && isEmpty(config.childDaytimePrice)) {
    return 'Child daytime price is required for CHILD pax type but not defined';
  }

  if (!hasChild && !isEmpty(config.childDaytimePrice)) {
    return 'Child daytime price is defined but CHILD pax type is not allowed';
  }

  return null;
};

const staycationViolation = (config: BasePriceConfiguration): string | null => {
  if (
    isEmpty(config.price) ||
    isEmpty(config.negotiatedPrice) ||
    isEmpty(config.staycationPrice) ||
    !isEmpty(config.daytimePrice) ||
    !isEmpty(config.childPrice) ||
    !isEmpty(config.childDaytimePrice)
  ) {
    return 'Price configuration is not correct for originals';
  }

  return null;
};

// Required-but-missing price fields for the active rule — drives the inline red
// highlight on the matching inputs (missingFields / fieldsByDay).
const missingPriceFields = (
  config: BasePriceConfiguration,
  mode: PriceDifferentiationModeType,
  hasAdult: boolean,
  hasChild: boolean,
  isAlwaysIncluded: boolean,
  isOriginal: boolean
): string[] => {
  const missing: string[] = [];

  if (isOriginal) {
    if (isEmpty(config.price)) {
      missing.push('price');
    }

    if (isEmpty(config.negotiatedPrice)) {
      missing.push('negotiatedPrice');
    }

    if (isEmpty(config.staycationPrice)) {
      missing.push('staycationPrice');
    }

    return missing;
  }

  if (isAlwaysIncluded) {
    if (hasAdult && isEmpty(config.daytimePrice)) {
      missing.push('daytimePrice');
    }

    if (
      mode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME &&
      hasChild &&
      isEmpty(config.childDaytimePrice)
    ) {
      missing.push('childDaytimePrice');
    }

    return missing;
  }

  switch (mode) {
    case PRICE_DIFFERENTIATION_MODE.NONE:
      if (hasAdult && isEmpty(config.price)) {
        missing.push('price');
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD:
      if (hasAdult && isEmpty(config.price)) {
        missing.push('price');
      }

      if (hasChild && isEmpty(config.childPrice)) {
        missing.push('childPrice');
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME:
      if (hasAdult && isEmpty(config.price)) {
        missing.push('price');
      }

      if (hasAdult && isEmpty(config.daytimePrice)) {
        missing.push('daytimePrice');
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME:
      if (hasAdult && isEmpty(config.price)) {
        missing.push('price');
      }

      if (hasChild && isEmpty(config.childPrice)) {
        missing.push('childPrice');
      }

      if (hasAdult && isEmpty(config.daytimePrice)) {
        missing.push('daytimePrice');
      }

      if (hasChild && isEmpty(config.childDaytimePrice)) {
        missing.push('childDaytimePrice');
      }

      break;
  }

  return missing;
};

// ============================================================================
// Scope validators
// ============================================================================

const validateGuests = (
  formValues: ExperienceInitialFormType
): ValidationErrorResult<ValidationError> => {
  const pax = formValues?.allowedPaxTypes ?? [];
  const hasAdult = pax.includes(PAX_TYPE.ADULT);
  const hasChild = pax.includes(PAX_TYPE.CHILD);
  const mode =
    formValues?.openingConfiguration?.priceConfiguration
      ?.priceDifferentiationMode;

  const errors: ValidationError[] = [];

  if (!hasAdult && !hasChild) {
    return {
      isValid: false,
      errors: [{ message: 'At least one guest type must be allowed' }],
    };
  }

  // core-service: every price differentiation mode requires ADULT.
  if (!hasAdult) {
    errors.push({ message: 'At least Adults must be allowed' });
  }

  if (
    !hasChild &&
    (mode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD ||
      mode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME)
  ) {
    errors.push({
      message: 'Children must be allowed for this price configuration',
    });
  }

  return { isValid: errors.length === 0, errors };
};

const validateStock = (
  formValues: ExperienceInitialFormType
): ValidationErrorResult<StockValidationError> => {
  const stockConfig = formValues?.openingConfiguration?.stockConfiguration;

  if (!stockConfig) {
    return valid();
  }

  const isAlwaysIncluded = !!formValues?.isAlwaysIncludedWithNight;
  const isOriginal = isOriginalExperience(formValues);
  const errors: StockValidationError[] = [];

  if (isOriginal && stockConfig.type !== OPENING_CONFIGURATION_TYPE.CONSTANT) {
    return {
      isValid: false,
      errors: [
        {
          type: stockConfig.type,
          isClosed: false,
          message: 'Experience originals must be constant',
        },
      ],
    };
  }

  if (stockConfig.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    const stock = stockConfig.configuration?.stock;

    if (typeof stock === 'number' && stock < 0) {
      errors.push({
        type: OPENING_CONFIGURATION_TYPE.CONSTANT,
        isClosed: false,
        stock,
        message: 'Stock cannot be negative',
      });
    }

    if (
      (isAlwaysIncluded || isOriginal) &&
      stock !== null &&
      stock !== undefined
    ) {
      errors.push({
        type: OPENING_CONFIGURATION_TYPE.CONSTANT,
        isClosed: false,
        stock,
        message: isOriginal
          ? 'Stock is not infinite for staycation experience'
          : 'Stock configuration is not unlimited for always included with night experience',
      });
    }

    return { isValid: errors.length === 0, errors };
  }

  const days = stockConfig.days ?? {};
  const closedWithStock: DayOfWeek[] = [];
  const negative: DayOfWeek[] = [];
  const notUnlimited: DayOfWeek[] = [];

  ALL_DAYS.forEach((day) => {
    const dayConfig = days[day];

    if (!dayConfig) {
      return;
    }

    const stock = dayConfig.stock;

    if (dayConfig.isClosed) {
      if (typeof stock === 'number' && stock > 0) {
        closedWithStock.push(day);
      }

      return;
    }

    if (typeof stock === 'number' && stock < 0) {
      negative.push(day);
    }

    if (
      isAlwaysIncluded &&
      stock !== null &&
      stock !== undefined &&
      stock !== 0
    ) {
      notUnlimited.push(day);
    }
  });

  if (closedWithStock.length) {
    errors.push({
      type: OPENING_CONFIGURATION_TYPE.DAILY,
      isClosed: true,
      message: 'Stock is closed and stock is greater than 0',
      days: closedWithStock,
    });
  }

  if (negative.length) {
    errors.push({
      type: OPENING_CONFIGURATION_TYPE.DAILY,
      isClosed: false,
      message: 'Stock cannot be negative',
      days: negative,
    });
  }

  if (notUnlimited.length) {
    errors.push({
      type: OPENING_CONFIGURATION_TYPE.DAILY,
      isClosed: false,
      message:
        'Stock configuration is not unlimited for always included with night experience',
      days: notUnlimited,
    });
  }

  return { isValid: errors.length === 0, errors };
};

const validatePrice = (
  formValues: ExperienceInitialFormType
): ValidationErrorResult<PriceValidationError> => {
  const priceConfig = formValues?.openingConfiguration?.priceConfiguration;

  if (!priceConfig) {
    return valid();
  }

  // Price inputs are only shown/required when the activity is explicitly PAID.
  // Free of charge (and an unset cost) render no price field, and the submit
  // mapping produces a valid payload on its own (NONE price:0, or always-included
  // WITH_DAYTIME daytimePrice:0). Pax coherence is still checked in validateGuests.
  if (priceConfig.cost !== PRICE_COST_TYPE.PAID) {
    return valid();
  }

  const stockConfig = formValues?.openingConfiguration?.stockConfiguration;
  const pax = formValues?.allowedPaxTypes ?? [];
  const hasAdult = pax.includes(PAX_TYPE.ADULT);
  const hasChild = pax.includes(PAX_TYPE.CHILD);
  const mode = priceConfig.priceDifferentiationMode;
  const isAlwaysIncluded = !!formValues?.isAlwaysIncludedWithNight;
  const isOriginal = isOriginalExperience(formValues);

  if (isOriginal && priceConfig.type !== OPENING_CONFIGURATION_TYPE.CONSTANT) {
    return {
      isValid: false,
      errors: [
        {
          type: priceConfig.type,
          message: 'Experience originals must be constant',
          price: 0,
        },
      ],
    };
  }

  if (isOriginal && mode !== PRICE_DIFFERENTIATION_MODE.NONE) {
    return {
      isValid: false,
      errors: [
        {
          type: OPENING_CONFIGURATION_TYPE.CONSTANT,
          message:
            'Price differentiation mode is not NONE for originals experience',
          price: 0,
        },
      ],
    };
  }

  const evaluate = (config: BasePriceConfiguration): string | null => {
    if (isOriginal) {
      return staycationViolation(config);
    }

    if (isAlwaysIncluded) {
      return alwaysIncludedViolation(config, mode, hasAdult, hasChild);
    }

    return priceModeViolation(config, mode, hasAdult, hasChild);
  };

  if (priceConfig.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    const config = priceConfig.configuration ?? {};
    const message = evaluate(config);

    if (!message) {
      return valid();
    }

    return {
      isValid: false,
      errors: [
        {
          type: OPENING_CONFIGURATION_TYPE.CONSTANT,
          message,
          price: 0,
          missingFields: missingPriceFields(
            config,
            mode,
            hasAdult,
            hasChild,
            isAlwaysIncluded,
            isOriginal
          ),
        },
      ],
    };
  }

  const errorsByMessage: Record<
    string,
    { days: DayOfWeek[]; fieldsByDay: Partial<Record<DayOfWeek, string[]>> }
  > = {};

  ALL_DAYS.forEach((day) => {
    const { isClosed } = getStockForDay(stockConfig, day);

    if (isClosed) {
      return;
    }

    const config = getPriceForDay(priceConfig, day);
    const message = evaluate(config);

    if (!message) {
      return;
    }

    if (!errorsByMessage[message]) {
      errorsByMessage[message] = { days: [], fieldsByDay: {} };
    }

    errorsByMessage[message].days.push(day);

    const missing = missingPriceFields(
      config,
      mode,
      hasAdult,
      hasChild,
      isAlwaysIncluded,
      isOriginal
    );

    if (missing.length) {
      errorsByMessage[message].fieldsByDay[day] = missing;
    }
  });

  const errors: PriceValidationError[] = Object.entries(errorsByMessage).map(
    ([message, data]) => ({
      type: OPENING_CONFIGURATION_TYPE.DAILY,
      message,
      price: 0,
      days: data.days,
      fieldsByDay: data.fieldsByDay,
    })
  );

  return { isValid: errors.length === 0, errors };
};

export const validateOpeningConfiguration = (
  formValues: ExperienceInitialFormType
): FormValidationErrors => ({
  stockError: validateStock(formValues),
  priceError: validatePrice(formValues),
  guestsError: validateGuests(formValues),
});
